import { useCallback, useEffect, useState } from 'react';
import {
  clearBrowsingHistory as clearBrowsingHistoryApi,
  getBrowsingHistory as getBrowsingHistoryApi,
  recordBrowsingHistory as recordBrowsingHistoryApi,
  removeBrowsingHistory as removeBrowsingHistoryApi,
  type InteractionTargetType,
} from '@/api/interaction';
import { useAuth } from '@/hooks/useAuth';

export type HistoryItemType = InteractionTargetType;

export interface HistoryItem {
  id: string;
  type: HistoryItemType;
  title: string;
  image?: string;
  category?: string;
  visitedAt: string;
}

const HISTORY_UPDATED_EVENT = 'wanxiang_history_updated';

function emitHistoryUpdated() {
  window.dispatchEvent(new CustomEvent(HISTORY_UPDATED_EVENT));
}

export function useHistory(options?: { lazy?: boolean }) {
  const { user, isLoggedIn } = useAuth();
  const [items, setItems] = useState<HistoryItem[]>([]);
  const lazy = options?.lazy ?? false;

  const refresh = useCallback(async () => {
    if (!isLoggedIn || !user?.id) {
      setItems([]);
      return;
    }
    try {
      const res = await getBrowsingHistoryApi();
      const next = Array.isArray(res)
        ? res.map((item) => ({
            id: String(item.id),
            type: item.type,
            title: item.title || '',
            image: item.image || undefined,
            category: item.category || undefined,
            visitedAt: item.visitedAt || '',
          }))
        : [];
      setItems(next);
    } catch (err) {
      console.error('Failed to fetch browsing history:', err);
      setItems([]);
    }
  }, [isLoggedIn, user?.id]);

  useEffect(() => {
    if (!lazy) {
      void refresh();
    }
  }, [refresh, lazy]);

  useEffect(() => {
    const handleUpdated = () => {
      void refresh();
    };
    window.addEventListener(HISTORY_UPDATED_EVENT, handleUpdated);
    return () => {
      window.removeEventListener(HISTORY_UPDATED_EVENT, handleUpdated);
    };
  }, [refresh]);

  const record = useCallback(async (item: Omit<HistoryItem, 'visitedAt'>) => {
    if (!isLoggedIn || !user?.id) {
      return { success: false, message: '请先登录' };
    }
    const targetId = Number(item.id);
    if (!Number.isInteger(targetId) || targetId <= 0) {
      return { success: false, message: '目标 ID 不合法' };
    }
    try {
      await recordBrowsingHistoryApi({
        targetType: item.type,
        targetId,
        titleSnapshot: item.title || '',
        imageUrl: item.image || '',
        categorySnapshot: item.category || '',
      });
      emitHistoryUpdated();
      return { success: true };
    } catch (err) {
      console.error('Failed to record browsing history:', err);
      return { success: false, message: err instanceof Error ? err.message : '记录浏览历史失败' };
    }
  }, [isLoggedIn, user?.id]);

  const remove = useCallback(async (id: string, type: HistoryItemType) => {
    if (!isLoggedIn || !user?.id) {
      return { success: false, message: '请先登录' };
    }
    const targetId = Number(id);
    if (!Number.isInteger(targetId) || targetId <= 0) {
      return { success: false, message: '目标 ID 不合法' };
    }
    try {
      await removeBrowsingHistoryApi({ targetType: type, targetId });
      setItems((prev) => prev.filter((item) => !(item.id === String(targetId) && item.type === type)));
      emitHistoryUpdated();
      return { success: true, message: '记录已删除' };
    } catch (err) {
      console.error('Failed to remove browsing history:', err);
      return { success: false, message: err instanceof Error ? err.message : '删除浏览历史失败' };
    }
  }, [isLoggedIn, user?.id]);

  const clearAll = useCallback(async () => {
    if (!isLoggedIn || !user?.id) {
      return { success: false, message: '请先登录' };
    }
    try {
      await clearBrowsingHistoryApi();
      setItems([]);
      emitHistoryUpdated();
      return { success: true, message: '浏览历史已清空' };
    } catch (err) {
      console.error('Failed to clear browsing history:', err);
      return { success: false, message: err instanceof Error ? err.message : '清空浏览历史失败' };
    }
  }, [isLoggedIn, user?.id]);

  return { items, refresh, record, remove, clearAll };
}
